/**
 * Product Details Bar Component
 *
 * Displays product information in the chat header for Product Support conversations.
 * Renders each selected product as its own section for easier scanning.
 * Supports collapsing to show only product names via a chevron toggle.
 */

import { ChevronDown, ChevronUp, Dna, UserRoundCheck } from 'lucide-react-native';
import { useState } from 'react';
import { View } from 'react-native';

import { Box } from '@/components/ui/box';
import { HStack } from '@/components/ui/hstack';
import { Icon } from '@/components/ui/icon';
import { Pressable } from '@/components/ui/pressable';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import { type Product } from '@/constants/medicalOnDemand';
import { getAccessibilityProps } from '@/util/accessibilityProps';
import { getAccessibleHitSlop } from '@/util/accessibleTouchTarget';

type ProductDetailsBarProps = {
  product: Product;
  products?: Product[];
};

export function ProductDetailsBar({ product, products }: ProductDetailsBarProps) {
  const allProducts = products?.length ? products : [product];

  const [isExpanded, setIsExpanded] = useState(true);

  return (
    <View
      testID="product-details-bar"
      className="relative bg-background-0 px-8 py-2 shadow-bottom-sm">
      <Box className="flex flex-row gap-4 sm:gap-8">
        {allProducts.map((item) => (
          <VStack key={item.id} className="min-w-0 flex-1 gap-2">
            <Text className="font-medium text-xs text-typography-900">
              <Text className="font-medium text-xs uppercase text-typography-900">
                {item.brandName}
              </Text>
              {item.genericName ? (
                <Text className="font-medium text-xs normal-case text-typography-900">
                  {' '}
                  ({item.genericName})
                </Text>
              ) : null}
            </Text>

            {isExpanded && item.indication ? (
              <HStack className="items-start gap-2">
                <View accessibilityLabel="Indication">
                  <Icon as={UserRoundCheck} size="xs" className="mt-[2px] text-typography-500" />
                </View>
                <VStack className="flex-1 gap-0.5">
                  <Text className="text-xs text-typography-600">{item.indication}</Text>
                  {item.headerBullets?.map((bullet) => (
                    <HStack key={bullet} className="items-start gap-1 pl-1">
                      <Text className="text-xs text-typography-600">•</Text>
                      <Text className="flex-1 text-xs text-typography-600">{bullet}</Text>
                    </HStack>
                  ))}
                </VStack>
              </HStack>
            ) : null}

            {isExpanded && item.drugClass ? (
              <HStack className="items-center gap-2">
                <View accessibilityLabel="Drug class and target">
                  <Icon as={Dna} size="xs" className="text-typography-500" />
                </View>
                <Text className="text-xs text-typography-600">
                  {item.drugTarget ? `${item.drugTarget}-directed ` : ''}
                  {item.drugClass}
                </Text>
              </HStack>
            ) : null}
          </VStack>
        ))}
      </Box>

      <Pressable
        onPress={() => setIsExpanded((prev) => !prev)}
        hitSlop={getAccessibleHitSlop(16)}
        className="absolute right-2 top-2 h-4 w-6 items-center justify-center rounded active:bg-background-100 web:cursor-pointer web:hover:bg-background-50"
        {...getAccessibilityProps({
          role: 'button',
          label: isExpanded ? 'Collapse product details' : 'Expand product details',
        })}>
        <Icon as={isExpanded ? ChevronUp : ChevronDown} size="sm" className="text-typography-700" />
      </Pressable>
    </View>
  );
}
